<script>
import { mapActions, mapGetters } from 'vuex';
import { watch } from 'vue';
import { useConfigurableInsightsStore } from '@/V2/stores/pinia/decision-engine/configurable-insights.store';
import {
  iSpinner,
  iFeaturedIconTypes,
  iToggle,
  IToggleSizes,
  iSearchInput,
  iButton,
  iButtonSizes,
  iButtonTypes,
  iButtonStyles,
  iModal,
  iMenu,
  iMenuItem,
  iMenuTriggerAlignOptions,
  openModal,
  showToast,
  iSearchInputSizes,
} from '@inscribe/signal';
import RuleEngine from '@/utils/ruleEngine';
import DecisionEngineCard from '@/V2/decision-engine/DecisionEngineCard.vue';
import RuleSave from '@/V2/decision-engine/rules/RuleSave.vue';
import EmptyPage from '@/V2/components/EmptyPage';
import DecisionEngineItemDescription from '@/V2/decision-engine/DecisionEngineItemDescription.vue';
import NewConfigurationBankInsightModal from '@/V2/decision-engine/configurable-insights/NewConfigurationBankInsightModal.vue';
import PageHeader from '@/V2/components/PageHeader.vue';

export default {
  components: {
    DecisionEngineCard,
    EmptyPage,
    iSpinner,
    RuleSave,
    iToggle,
    iSearchInput,
    DecisionEngineItemDescription,
    iButton,
    iModal,
    iMenu,
    iMenuItem,
    NewConfigurationBankInsightModal,
    PageHeader,
  },
  data() {
    return {
      signalsStore: useConfigurableInsightsStore(),
      displayLink:
        'https://help.inscribe.ai/en/articles/7852994-understanding-inscribe-fraud-signals',
      iButtonSizes,
      iButtonTypes,
      iButtonStyles,
      iFeaturedIconTypes,
      iMenuTriggerAlignOptions,
      IToggleSizes,
      showNewRuleModal: false,
      newRule: null,
      ruleError: null,
      rulesToBeSaved: null,
      mode: '',
      isLoading: true,
      signalTypeIsDocument: true,
      searchQuery: '',
      insightToBeDeleted: null,
      iSearchInputSizes,
    };
  },
  computed: {
    ...mapGetters([
      'getRules',
      'getRulesSchema',
      'isOrgAdmin',
      'configurableRiskSignalsEnabled',
    ]),
    detectors() {
      if (this.signalTypeIsDocument) {
        return this.getRules.filter(
          (r) => r.default && RuleEngine.isSimpleDetectorRule(r),
        );
      }
      return this.signalsStore?.getConfigurableInsights || [];
    },
    visibleDetectors() {
      const query = this.searchQuery?.toLowerCase() || '';
      return this.detectors.filter((d) => {
        if (d.title) {
          // Insight: search in name, title, and description
          return (
            d.name?.toLowerCase().includes(query) ||
            d.title?.toLowerCase().includes(query) ||
            d.description?.toLowerCase().includes(query)
          );
        }
        // Rule: use standard filter
        return RuleEngine.shouldIncludeRule(d, this.searchQuery);
      });
    },
  },
  mounted() {
    // If returning from editing a custom insight, switch to Custom view
    if (
      this.signalsStore.getCurrentInsight ||
      this.$route.query.view === 'custom'
    ) {
      this.signalTypeIsDocument = false;
      this.signalsStore.setSelectedConfigurableInsight(null);
    }

    if (this.isOrgAdmin) {
      const promises = [this.getConfigRules()];

      Promise.all(promises).then(() => {
        this.isLoading = false;
      });

      if (this.configurableRiskSignalsEnabled) {
        this.signalsStore.fetchConfigurableInsights();
      }

      // Watch for feature flag changes (race condition handling)
      watch(
        () => this.configurableRiskSignalsEnabled,
        (newValue) => {
          if (newValue) {
            this.signalsStore.fetchConfigurableInsights();
          }
        },
      );
    }
  },
  methods: {
    ...mapGetters(['getRuleIndex']),
    ...mapActions({
      getConfigRules: 'getConfigRules',
    }),

    getRuleTitle(rule) {
      // For custom insights, use signal_name
      if (!this.signalTypeIsDocument) {
        return rule.signal_name;
      }
      return RuleEngine.getRuleTitle(rule);
    },

    getRuleSubtitle(rule) {
      // For custom insights, use configuration description
      if (!this.signalTypeIsDocument) {
        return rule.configuration?.description || rule.description || '';
      }

      const map = {
        DETECTOR_ADOBE_FONTS:
          'Identifies and flags documents using fonts from the Adobe Fonts service that are unusual for the document type',
        DETECTOR_EDITED_WHILE_SCANNED:
          'Flags documents that show signs of being modified or edited during the scanning process',
        DETECTOR_EXIF_DATA_MODIFIED:
          'Detects metadata anomalies that suggest possible file manipulation',
        DETECTOR_QPDF:
          'Analyzes PDFs for alterations that are typical of QPDF library manipulations',
        DETECTOR_REJECTED_CUSTOMER:
          'Flags documents previously submitted by individuals or entities that have previously been rejected',
        DETECTOR_TEXT_LAYER_TEXT:
          'Identifies files with added text on top of images within PDFs',
        DETECTOR_AMOUNT_FORMAT_ANOMALY:
          'Flags document text with unusual numerical formats that may indicate fraudulent activity',
        DETECTOR_ANNOTATIONS:
          'Flags annotated documents that may manipulate or obscure information',
        DETECTOR_DOCSTORE_TEMPLATE:
          "Compares a document's mathematical representation with known templates from online document stores",
        DETECTOR_MASKING:
          'Identifies regions where text is hidden using annotations or solid color blocks in PDFs',
        DETECTOR_XRAY:
          'Compares the oldest and most recent versions of a PDF, flagging differences and highlighting them in an x-ray viewer',
        DETECTOR_SEEN_BEFORE_NOT_FRAUDULENT:
          'Detects if the exact same document has been uploaded more than once for the same organization',
        DETECTOR_FLATTENED_PDF:
          'Detects when a PDF has been flattened, merging all layers into one, potentially indicating data manipulation',
        DETECTOR_SOFTWARE_BLACKLIST:
          'Flags documents where consumer software has been used, indicating potential fraud',
        DETECTOR_BLACKLIST:
          'Flags documents matching names and addresses on a user-defined blocklist as potentially fraudulent',
        DETECTOR_COPYCAT_IMAGE:
          'Flags images as fraudulent if highly similar to others with different names or identities in the database',
        DETECTOR_COPYCAT_TEXT:
          'Flags documents with highly similar text to others associated with different identities in the database',
        DETECTOR_TRANSACTIONS_DO_NOT_BALANCE:
          'Flags bank statements where balances do not reconcile with transactions',
        DETECTOR_TEXT_ALIGNMENT:
          'Flags bank statements with text that is not aligned in key regions of the document',
        DETECTOR_SUMMARY_NOT_BALANCED:
          'Flags bank statements where the values in the summary section do not reconcile',
        DETECTOR_PAYSLIP_MISCALCULATION:
          'Flags payslips where there the deduction values do not reconcile with the totals',
        DETECTOR_REPEAT_ACCOUNT_NUMBER:
          'Flags documents where the account number is repeated under documents with a different identity',
        DETECTOR_HIGH_RISK_CUSTOMER_SEEN_BEFORE:
          'Flags documents previously submitted by customers associated with at least one high risk document',
        DETECTOR_REPEAT_BALANCES:
          'Flags bank statements where key amounts are repeated under documents with a different identity',
        DETECTOR_AI_GENERATED: 'Flags documents that are likely AI generated',
        DETECTOR_INCONSISTENT_EIN:
          'Flags tax forms/letters of type CP-575 and 147C where there is more than one EIN',
        DETECTOR_STRIPPED_METADATA:
          'Flags documents with empty metadata fields, which may indicate stripped metadata used to hide signs of editing',
        DETECTOR_PDF_STRUCTURE_ANOMALY:
          'Flags documents where the internal structure shows anomalies',
        DETECTOR_SUSPICIOUS_PDF_METADATA:
          'Flags documents with suspicious PDF metadata',
        DETECTOR_SUSPICIOUS_PDF_OWNER:
          'Flags documents with a suspicious PDF owner',
        DETECTOR_SHARPNESS_INCONSISTENCY:
          'Flags images with sharpness inconsistencies in the text which may indicate fraudulent edits',
      };

      return map[rule.name] || rule.description || rule.name;
    },

    onDetectorEnabledToggle(params) {
      this.$emit('hide-tooltips');

      if (this.signalTypeIsDocument) {
        this.updateDocumentRuleEnabled(params.item, params.value);
      } else {
        this.updateCustomInsightEnabled(params.item, params.value);
      }
    },

    updateDocumentRuleEnabled(item, value) {
      const foundRuleIdx = this.getRuleIndex()(item);
      if (foundRuleIdx > -1) {
        this.rulesToBeSaved = [
          ...this.getRules.slice(0, foundRuleIdx),
          { ...item, enabled: value },
          ...this.getRules.slice(foundRuleIdx + 1),
        ];
      }
    },

    updateCustomInsightEnabled(item, value) {
      this.signalsStore.updateConfigurableInsight({
        ...item,
        configuration: {
          ...item.configuration,
          enabled: value,
        },
      });
    },
    onSignalTypeToggle(value) {
      this.signalTypeIsDocument = value;
    },
    onSearchQueryChanged(value) {
      this.searchQuery = value;
    },
    onInsightCardClick({ item }) {
      if (!this.signalTypeIsDocument && this.isOrgAdmin) {
        this.signalsStore.setSelectedConfigurableInsight(item);
        this.$router.push({ name: 'decision-engine-signals-edit' });
      }
    },
    createBankAccountInsight() {
      this.$refs.newConfigurationBankInsightModal.openModal();
    },

    onEditClick({ item }) {
      if (!this.signalTypeIsDocument) {
        const insight =
          this.signalsStore.getConfigurableInsightsByUuid[item.uuid];
        this.signalsStore.setSelectedConfigurableInsight(insight);
        this.$router.push({ name: 'decision-engine-signals-edit' });
      }
    },
    onDeleteClick({ item }) {
      this.insightToBeDeleted = item;
      openModal('delete-insight-modal');
    },
    async onDeleteInsight() {
      if (!this.insightToBeDeleted) {
        return;
      }

      const insightName = this.insightToBeDeleted?.signal_name;

      try {
        await this.signalsStore.deleteConfigurableInsight(
          this.insightToBeDeleted,
        );
        this.insightToBeDeleted = null;

        showToast({
          title: 'Insight Deleted',
          description: `"${insightName}" has been successfully deleted.`,
          type: 'success',
        });
      } catch (error) {
        showToast({
          title: 'Error Deleting Insight',
          description:
            error?.response?.data?.message ||
            `Failed to delete "${insightName}".`,
          type: 'error',
        });
      }
    },
    onDeleteClose() {
      this.insightToBeDeleted = null;
    },
  },
};
</script>

<template>
  <div class="detectors-page d-flex flex-column">
    <PageHeader title="Insights">
      <template #primary-actions>
        <i-menu
          v-if="configurableRiskSignalsEnabled && isOrgAdmin"
          :close-on-item-click="true"
          :trigger-align="iMenuTriggerAlignOptions.LEFT"
          :width="300"
        >
          <template #trigger>
            <i-button
              text="New Insight"
              size="md"
              right-icon="medium-chevron-down"
              data-testid="new-insight-button"
              data-dd-action-name="new-insight-button"
            />
          </template>
          <template #items>
            <i-menu-item
              label="Transaction Insight"
              icon="medium-add-circle"
              description="Monitor patterns of account activity"
              data-testid="transaction-insight-option"
              @item-click="createBankAccountInsight"
            />
            <i-menu-item
              label="Custom Insight"
              icon="medium-add-circle"
              description="Additional insight types coming soon"
              :disabled="true"
            />
          </template>
        </i-menu>
      </template>
    </PageHeader>

    <!-- page content -->
    <div class="decision-engine-page-content d-flex flex-column i-gap-11">
      <!-- item description -->
      <decision-engine-item-description
        :title="signalTypeIsDocument ? 'Document Insights' : 'Custom Insights'"
        :description="
          signalTypeIsDocument
            ? 'Verifies individual documents for authenticity and signs of tampering. We analyze file metadata, font consistency, and image compression artifacts to detect potential fraud with varying severity levels.'
            : 'Define your own transaction-based signals. You can create rules to identify specific patterns in transaction activity, balance history, or spending categories that you want to flag or review.'
        "
      />

      <!-- type selection -->
      <i-toggle
        v-if="configurableRiskSignalsEnabled"
        left-label="Document"
        right-label="Custom"
        :toggle-value="signalTypeIsDocument"
        :full-width="false"
        :size="IToggleSizes.MEDIUM"
        class="signals-type-toggle"
        data-testid="insights-type-toggle"
        data-dd-action-name="decision-engine-signals-type-toggle"
        @value-update="onSignalTypeToggle"
      ></i-toggle>

      <!-- input search filter -->
      <i-search-input
        input-placeholder="Search Insights"
        :debounce-time="200"
        :model-value="searchQuery"
        :size="iSearchInputSizes.LARGE"
        data-dd-action-name="detectors-search-input"
        @update:model-value="onSearchQueryChanged"
      />

      <!-- loading screen-->
      <div
        v-if="isLoading"
        class="d-flex i-my-5 w-100 align-items-center justify-content-center"
      >
        <i-spinner></i-spinner>
      </div>

      <div
        v-else
        :class="{ 'items-list': visibleDetectors.length > 0 }"
        class="rules-list i-radius-4xl d-flex flex-column"
        data-testid="default-rules"
      >
        <!-- empty page -->
        <empty-page
          v-if="visibleDetectors.length === 0 && !signalTypeIsDocument"
          class="d-flex flex-grow-1 empty-page"
          icon="large-file-settings"
          title="Custom Signals"
          subtitle="All of your custom signals will appear here."
          button-text="New Insight"
          @on-button-click="createBankAccountInsight"
        ></empty-page>

        <template v-else>
          <decision-engine-card
            v-for="(rule, ruleIndex) in visibleDetectors"
            :key="rule.uuid || rule.name || ruleIndex"
            data-testid="insight-card"
            :title="getRuleTitle(rule)"
            :subtitle="getRuleSubtitle(rule)"
            :item="rule"
            icon="medium_solid_alert_triangle"
            :icon-type="iFeaturedIconTypes.ERROR"
            tooltip-toggle-on="Disable Rule"
            tooltip-toggle-off="Enable Rule"
            tooltip-edit="Edit Signal"
            tooltip-delete="Delete Signal"
            :switch-visible="isOrgAdmin"
            :switch-value="
              signalTypeIsDocument ? rule.enabled : rule.configuration?.enabled
            "
            :display-tools="!signalTypeIsDocument && isOrgAdmin"
            :with-hover-state="true"
            @switch-change="onDetectorEnabledToggle"
            @card-click="onInsightCardClick"
            @edit-click="onEditClick"
            @delete-click="onDeleteClick"
          ></decision-engine-card>
        </template>
      </div>
    </div>

    <!-- rules save -->
    <rule-save :new-rules-collection="rulesToBeSaved"></rule-save>

    <!-- New Insight Modal -->
    <new-configuration-bank-insight-modal
      v-if="configurableRiskSignalsEnabled && isOrgAdmin"
      ref="newConfigurationBankInsightModal"
    ></new-configuration-bank-insight-modal>

    <!-- Delete Insight Modal -->
    <i-modal
      modal-id="delete-insight-modal"
      data-testid="delete-insight-modal"
      title="Delete Insight"
      confirm-button-text="Delete"
      type="error"
      @on-submit="onDeleteInsight"
      @on-close="onDeleteClose"
    >
      <template #body-content>
        <div class="delete-modal-content">
          Are you sure you want to delete "{{
            insightToBeDeleted?.signal_name
          }}"? This action cannot be undone.
        </div>
      </template>
    </i-modal>
  </div>
</template>

<style lang="scss" scoped>
.detectors-page {
  .signals-type-toggle {
    width: fit-content;
  }

  .empty-page {
    min-height: 300px;
  }
}
</style>
